'use client';

/* ============================================================================
 * PricingV2 - the /pricing/ page.
 * ============================================================================
 * Instant Site has no standalone price: it ships with the Prostay Booking
 * Engine inside the All-in-One platform. This page tells that story:
 *   1  Hero      - "the best price is no price"
 *   2  Included  - the full included list
 *   3  How       - three routes to getting Instant Site
 *   4  FAQ       - pricing fine print
 *   5  Closing   - demo CTA
 * Copy lives in messages/<locale>.json under Pages.Pricing.
 * ========================================================================= */

import React, {useState} from 'react';
import {useTranslations} from 'next-intl';
import {Link} from '@/i18n/navigation';
import {Reveal, RevealStagger, RevealLine} from '@/components/v2/Reveal';

const NS = 'Pages.Pricing';
const TOTAL = 5;

function Container({children, className = ''}) {
  return <div className={`container-page ${className}`}>{children}</div>;
}

function SectionAnchor({id, label}) {
  return (
    <Reveal className="mb-10 flex items-center gap-3 text-foreground-subtle">
      <span className="font-mono text-[11px] tracking-[0.16em] uppercase">
        {String(id).padStart(2, '0')} / {String(TOTAL).padStart(2, '0')}
      </span>
      <RevealLine className="block h-px flex-1 bg-border" delay={0.1} />
      <span className="font-mono text-[11px] tracking-[0.16em] uppercase">{label}</span>
    </Reveal>
  );
}

function CheckIcon({className = ''}) {
  return (
    <svg className={className} viewBox="0 0 14 14" fill="none" aria-hidden>
      <path
        d="M11.5 4L5.5 10L2.5 7"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function SectionHero() {
  const t = useTranslations(NS);
  return (
    <section className="bg-hero-mesh relative overflow-hidden pt-32 pb-20 md:pt-40 md:pb-24">
      <Container>
        <SectionAnchor id={1} label={t('anchors.hero')} />
        <Reveal eager>
          <p className="fig-label">{t('hero.eyebrow')}</p>
        </Reveal>
        <Reveal delay={0.06} eager>
          <h1 className="headline-hero mt-5 max-w-[20ch] text-foreground">
            {t('hero.headline')}
          </h1>
        </Reveal>
        <Reveal delay={0.12} eager>
          <p className="lead mt-6 max-w-[62ch]">{t('hero.lead')}</p>
        </Reveal>
      </Container>
    </section>
  );
}

function SectionIncluded() {
  const t = useTranslations(NS);
  const bullets = t.raw('included.bullets');
  return (
    <section className="section-rhythm-sm pb-24">
      <Container>
        <SectionAnchor id={2} label={t('anchors.included')} />
        <div className="surface-elevated relative overflow-hidden p-8 md:p-12">
          <div className="glow-brand-soft" />
          <div className="relative">
            <Reveal>
              <h2 className="headline-section max-w-[22ch] text-foreground">
                {t('included.headline')}
              </h2>
            </Reveal>
            <Reveal delay={0.06}>
              <p className="lead mt-4 max-w-[52ch]">{t('included.lead')}</p>
            </Reveal>
            <RevealStagger
              as="ul"
              mode="clone"
              className="mt-10 grid grid-cols-1 gap-x-10 gap-y-4 border-t border-border pt-8 sm:grid-cols-2"
              gap={0.04}
            >
              {bullets.map((b) => (
                <li key={b} className="flex items-start gap-3 text-[15px] leading-relaxed text-foreground-muted">
                  <CheckIcon className="mt-1.5 size-3.5 flex-shrink-0 text-primary" />
                  <span>{b}</span>
                </li>
              ))}
            </RevealStagger>
          </div>
        </div>
      </Container>
    </section>
  );
}

function SectionHow() {
  const t = useTranslations(NS);
  const items = t.raw('how.items');
  return (
    <section className="section-rhythm bg-background">
      <Container>
        <SectionAnchor id={3} label={t('anchors.how')} />
        <div className="mb-12 grid grid-cols-1 gap-8 md:grid-cols-12">
          <div className="md:col-span-7">
            <Reveal>
              <h2 className="headline-section max-w-[20ch] text-foreground">
                {t('how.headline')}
              </h2>
            </Reveal>
          </div>
          <div className="md:col-span-5 md:self-end">
            <Reveal delay={0.06}>
              <p className="lead">{t('how.lead')}</p>
            </Reveal>
          </div>
        </div>
        <RevealStagger
          as="ul"
          mode="clone"
          className="grid grid-cols-1 divide-y divide-border border-y border-border md:grid-cols-3 md:divide-y-0 md:divide-x"
          gap={0.06}
        >
          {items.map((item, i) => (
            <li key={item.title} className="p-7 md:p-8">
              <p className="font-mono text-[11px] tracking-[0.16em] uppercase text-foreground-subtle">
                {String(i + 1).padStart(2, '0')}
              </p>
              <h3 className="mt-3 text-xl font-medium tracking-tight text-foreground">
                {item.title}
              </h3>
              <p className="mt-3 text-[14px] leading-relaxed text-foreground-muted">
                {item.body}
              </p>
            </li>
          ))}
        </RevealStagger>
      </Container>
    </section>
  );
}

function FaqItem({item, open, onToggle}) {
  return (
    <li className="border-b border-border">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className="flex w-full items-start justify-between gap-6 py-6 text-left"
      >
        <span className="text-[16px] font-medium tracking-tight text-foreground md:text-lg">
          {item.q}
        </span>
        <span
          aria-hidden
          className={`mt-1 flex size-6 flex-shrink-0 items-center justify-center rounded-full border border-border text-foreground transition-transform duration-200 ${
            open ? 'rotate-45' : ''
          }`}
        >
          <svg viewBox="0 0 12 12" className="size-3" fill="none">
            <path d="M6 1v10M1 6h10" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
          </svg>
        </span>
      </button>
      <div
        className={`grid transition-all duration-300 ease-out ${
          open ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
        }`}
      >
        <div className="overflow-hidden">
          <p className="max-w-[70ch] pb-6 text-[14.5px] leading-relaxed text-foreground-muted">
            {item.a}
          </p>
        </div>
      </div>
    </li>
  );
}

function SectionFaq() {
  const t = useTranslations(NS);
  const items = t.raw('faq.items');
  const [openIndex, setOpenIndex] = useState(0);
  return (
    <section className="section-rhythm">
      <Container>
        <SectionAnchor id={4} label={t('anchors.faq')} />
        <div className="grid grid-cols-1 gap-12 md:grid-cols-12 md:gap-10">
          <div className="md:col-span-4">
            <Reveal>
              <h2 className="headline-section max-w-[14ch] text-foreground">
                {t('faq.headline')}
              </h2>
            </Reveal>
          </div>
          <div className="md:col-span-8">
            <Reveal delay={0.08}>
              <ul className="border-t border-border">
                {items.map((item, i) => (
                  <FaqItem
                    key={item.q}
                    item={item}
                    open={openIndex === i}
                    onToggle={() => setOpenIndex(openIndex === i ? -1 : i)}
                  />
                ))}
              </ul>
            </Reveal>
          </div>
        </div>
      </Container>
    </section>
  );
}

function SectionClosing() {
  const t = useTranslations(NS);
  return (
    <section className="section-rhythm-lg bg-background">
      <Container>
        <SectionAnchor id={5} label={t('anchors.closing')} />
        <Reveal>
          <h2 className="headline-manifesto max-w-[18ch] text-foreground">
            {t('closing.headline')}
          </h2>
        </Reveal>
        <Reveal delay={0.08}>
          <p className="lead mt-6 max-w-[52ch]">{t('closing.body')}</p>
        </Reveal>
        <Reveal delay={0.14}>
          <div className="mt-10 flex flex-wrap items-center gap-3">
            <Link href="/request-demo/" className="cta-pill-brand">
              {t('closing.primaryCta')} <span aria-hidden>&rarr;</span>
            </Link>
            <Link href="/features/" className="cta-text">
              {t('closing.secondaryCta')} <span aria-hidden>&rarr;</span>
            </Link>
          </div>
        </Reveal>
      </Container>
    </section>
  );
}

export default function PricingV2() {
  return (
    <>
      <SectionHero />
      <SectionIncluded />
      <SectionHow />
      <SectionFaq />
      <SectionClosing />
    </>
  );
}
